/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import Button from 'react-bootstrap/Button'
import Toast from 'components/atoms/Toast'
import {
  AvailableAccessPolicy,
  useGetAvailableAccessPoliciesQuery,
  useGetUserAccessPoliciesQuery,
  useSetUserAccessPoliciesMutation,
} from 'store/services/accessPolicies'

interface Props {
  userId: number
  targetIsSuperadmin: boolean
  targetName?: string
}

const c = {
  accent: '#2286f2',
  accentSoft: '#eef5ff',
  border: '#e3e7eb',
  ink: '#1d242b',
  sub: '#6d757e',
  track: '#f0f2f4',
  amberBg: '#fff8e8',
  amberBorder: '#f0dca6',
  amberInk: '#9a6a10',
}

const styles = {
  intro: css`
    font-size: 13px;
    color: ${c.sub};
    margin: 0 0 16px;
    max-width: 640px;
  `,
  summary: css`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid ${c.border};
    background: #fafbfc;
    margin-bottom: 16px;
  `,
  summaryActive: css`
    border-color: ${c.accent};
    background: ${c.accentSoft};
  `,
  summaryIcon: css`
    flex: 0 0 auto;
    margin-top: 1px;
  `,
  summaryTitle: css`
    font-size: 13px;
    font-weight: 700;
    color: ${c.ink};
    margin: 0 0 4px;
    letter-spacing: 0.01em;
  `,
  summaryText: css`
    font-size: 13px;
    color: ${c.sub};
    margin: 0;
  `,
  chips: css`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  `,
  chip: css`
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: ${c.accent};
    background: #fff;
    border: 1px solid ${c.accent};
    border-radius: 999px;
    padding: 3px 10px;
  `,
  chipAnd: css`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${c.sub};
    align-self: center;
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
  `,
  tile: css`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid ${c.border};
    background: #fff;
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
    &:hover {
      border-color: #c7d2dd;
      background: #fbfcfe;
    }
    &:focus-within {
      outline: 2px solid ${c.accent};
      outline-offset: 1px;
    }
  `,
  tileSelected: css`
    border-color: ${c.accent};
    background: ${c.accentSoft};
    box-shadow: 0 1px 2px rgba(34, 134, 242, 0.14);
    &:hover {
      border-color: ${c.accent};
      background: ${c.accentSoft};
    }
  `,
  srInput: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  `,
  mark: css`
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 2px solid #c7d2dd;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  `,
  markSelected: css`
    border-color: ${c.accent};
    background: ${c.accent};
  `,
  tileLabel: css`
    flex: 1 1 auto;
    font-size: 14px;
    font-weight: 600;
    color: ${c.ink};
    line-height: 1.3;
  `,
  scope: css`
    flex: 0 0 auto;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${c.sub};
    background: ${c.track};
    border-radius: 5px;
    padding: 3px 7px;
  `,
  banner: css`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid ${c.amberBorder};
    background: ${c.amberBg};
    margin-bottom: 16px;
  `,
  bannerTitle: css`
    font-size: 13px;
    font-weight: 700;
    color: ${c.amberInk};
    margin: 0 0 3px;
  `,
  bannerText: css`
    font-size: 13px;
    color: #7a5a17;
    margin: 0;
  `,
  footer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid ${c.border};
  `,
  unsaved: css`
    font-size: 12px;
    font-weight: 600;
    color: ${c.amberInk};
  `,
  muted: css`
    font-size: 13px;
    color: ${c.sub};
    padding: 8px 0;
  `,
}

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2l2.2 2.2 4.8-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const FunnelIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 4.5h14L11.5 11v4.5L8.5 17v-6L3 4.5z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
)

const GlobeIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7.2" stroke={color} strokeWidth="1.6" />
    <path d="M2.8 10h14.4M10 2.8c2 2 2 12.4 0 14.4-2-2-2-12.4 0-14.4z" stroke={color} strokeWidth="1.6" />
  </svg>
)

const ShieldIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M10 2.5l6 2v4.2c0 4-2.6 6.8-6 8.8-3.4-2-6-4.8-6-8.8V4.5l6-2z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M7.4 10l1.8 1.8 3.4-3.6" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const sortedKey = (keys: string[]): string => [...keys].sort().join(',')

/**
 * Superadmin-only editor for the order access policies applied to a LAB user.
 *
 * Policies stack: each one narrows access, so a user only sees orders that match all
 * of them. No policies = full access. Saves independently of the surrounding user form.
 */
const AccessPolicyAssignment = ({ userId, targetIsSuperadmin, targetName }: Props) => {
  const { data: available = [], isLoading: loadingPolicies } = useGetAvailableAccessPoliciesQuery()
  // Always reflect the server's assignments when (re)opening a user — this is a security-sensitive
  // panel, so never render a cached snapshot from a previous visit.
  const { data: assigned, isLoading: loadingAssigned } = useGetUserAccessPoliciesQuery(
    { userId },
    { refetchOnMountOrArgChange: true },
  )
  const [setUserPolicies, { isLoading: saving }] = useSetUserAccessPoliciesMutation()

  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    setSelected(assigned?.policyKeys ?? [])
  }, [assigned])

  const isDirty = useMemo(
    () => sortedKey(selected) !== sortedKey(assigned?.policyKeys ?? []),
    [selected, assigned],
  )

  const selectedPolicies = useMemo(
    () => available.filter((p) => selected.includes(p.key)),
    [available, selected],
  )

  const toggle = (key: string, checked: boolean) => {
    setSelected((prev) => (checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)))
  }

  const onSave = async () => {
    try {
      await setUserPolicies({ userId, policyKeys: selected }).unwrap()
      Toast({ type: 'success', label: 'Access policies updated.' })
    } catch (err: any) {
      Toast({ type: 'error', label: err?.data?.message || 'Failed to update access policies.' })
    }
  }

  const loading = loadingPolicies || loadingAssigned
  const restricted = selectedPolicies.length > 0
  const subjectCap = targetName || 'This user' // starts a sentence
  const subjectLow = targetName || 'this user' // mid-sentence
  // Only surface the entity scope once more than one kind of catalog exists — today
  // every policy is order-scoped, so a chip on each tile would just be noise.
  const showScope = useMemo(() => new Set(available.map((p) => p.entity)).size > 1, [available])

  const renderTile = (policy: AvailableAccessPolicy) => {
    const isSelected = selected.includes(policy.key)
    return (
      <label key={policy.key} css={[styles.tile, isSelected && styles.tileSelected]}>
        <input
          css={styles.srInput}
          type="checkbox"
          checked={isSelected}
          onChange={(e) => toggle(policy.key, e.target.checked)}
        />
        <span css={[styles.mark, isSelected && styles.markSelected]}>
          {isSelected && <CheckIcon />}
        </span>
        <span css={styles.tileLabel}>{policy.label}</span>
        {showScope && (
          <span css={styles.scope}>{policy.entity === 'order' ? 'Orders' : policy.entity}</span>
        )}
      </label>
    )
  }

  return (
    <div className="card card-primary p-2">
      <div className="card-header">
        <h3 className="card-title">ORDER ACCESS POLICIES</h3>
      </div>
      <div className="card-body">
        <p css={styles.intro}>
          Choose which orders {subjectLow} can view and open. Each policy narrows access — the user
          only sees orders that match <strong>all</strong> of the ones you select. With none
          selected, they see every order.
        </p>

        {targetIsSuperadmin && (
          <div css={styles.banner} role="note">
            <span css={styles.summaryIcon}>
              <ShieldIcon color={c.amberInk} />
            </span>
            <div>
              <p css={styles.bannerTitle}>Superadmins aren’t limited by access policies</p>
              <p css={styles.bannerText}>
                {subjectCap} can see and manage every order regardless of what’s selected here.
                Assignments still save and take effect if superadmin is removed.
              </p>
            </div>
          </div>
        )}

        {!targetIsSuperadmin && !loading && (
          <div css={[styles.summary, restricted && styles.summaryActive]}>
            <span css={styles.summaryIcon}>
              {restricted ? <FunnelIcon color={c.accent} /> : <GlobeIcon color={c.sub} />}
            </span>
            <div css={css`flex: 1 1 auto;`}>
              <p css={styles.summaryTitle}>{restricted ? 'Restricted access' : 'Full access'}</p>
              <p css={styles.summaryText}>
                {restricted
                  ? 'Sees only orders that match all of these:'
                  : `${subjectCap} can see and open every order — no restrictions applied.`}
              </p>
              {restricted && (
                <div css={styles.chips}>
                  {selectedPolicies.map((p, i) => (
                    <span key={p.key} css={css`display: inline-flex; gap: 6px; align-items: center;`}>
                      {i > 0 && <span css={styles.chipAnd}>and</span>}
                      <span css={styles.chip}>{p.label}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {loading && <div css={styles.muted}>Loading policies…</div>}

        {!loading && available.length === 0 && (
          <div css={styles.muted}>No access policies have been defined yet.</div>
        )}

        {!loading && available.length > 0 && <div css={styles.grid}>{available.map(renderTile)}</div>}

        {!loading && available.length > 0 && (
          <div css={styles.footer}>
            <span css={styles.unsaved}>{isDirty ? 'Unsaved changes' : ''}</span>
            <Button type="button" variant="primary" disabled={saving || !isDirty} onClick={onSave}>
              {saving ? 'Saving…' : 'Save access policies'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AccessPolicyAssignment
